/* globals window */
import React from 'react';
import {
  Button,
  FormWithRedirect,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  TextField,
  TextInput,
  useNotify,
  useRefresh,
} from 'react-admin';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { Box, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconCancel from '@material-ui/icons/Cancel';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';

import UnifiedAddService from './UnifiedAddService';
import { apiPut } from '../../brainApi';

const useStyles = makeStyles(() => ({
  btnCancel: {
    backgroundColor: '#fdfdfd',
    height: 37,
    marginRight: 10,
    padding: 10,
    color: '#212B36',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    borderRadius:8
  },
  selectRoot: {
    position: 'absolute',
    width: 500,
  },
  actionBox:{
    position:'absolute',
    top:32,
    right:12
  }
}));

export const EditServicesHeader = ({
  showUpsellStatus = true,
  showStatus = true,
}) => (
  <TableRow className="mdHeader">
    <TableCell colSpan={9}>
      <Box display="flex">
        <Box pr={0.8} className="editInput" width="20%">
          <Typography variant="body2" align="center">
            Service
          </Typography>
        </Box>
        {showStatus && (
          <Box pr={0.8} className="editInput" width="15%">
            <Typography variant="body2" align="center">
              Status
            </Typography>
          </Box>
        )}
        {showUpsellStatus && (
          <Box pr={0.8} className="editInput" width="24%">
            <Typography variant="body2" align="center">
              Upsell Status
            </Typography>
          </Box>
        )}
        <Box pr={0.8} className="editInput" width="15%">
          <Typography variant="body2" align="center">
            Service Duration (Hours)
          </Typography>
        </Box>
        <Box pr={0.8} className="editInput" width="15%">
          <Typography variant="body2" align="center">
            Labor Charge
          </Typography>
        </Box>
        <Box pr={0.8} className="editInput" width="15%">
          <Typography variant="body2" align="center">
            Parts Charge
          </Typography>
        </Box>
        <Box pr={0.8} className="editInput" width="3%">
          <Typography variant="body2">&nbsp;</Typography>
        </Box>
      </Box>
    </TableCell>
  </TableRow>
);

const UnifiedEditServices = ({ setEditServices, ...props }) => {
  const { record } = props;
  const { id } = record;
  record.newServices = null;
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleSubmit = async (values) => {
    const newValues = values;
    const payload = {
      is_created_by_admin: true,
      vehicle_id: record?.vehicle_id,
      services: [],
    };
    // Add new services as service_offerings
    if (newValues?.newServices?.length > 0) {
      payload.service_offerings = newValues.newServices;
    }
    // Add existing services as services
    if (newValues?.services?.length > 0) {
      payload.services = values.services.map((service) => {
        if (service.upsell_status) {
          return { ...service, is_upsell: true };
        }
        return { ...service, is_upsell: false };
      });
    }
    apiPut(`/scheduled-work/${id}`, payload)
      .then(() => {
        setEditServices(false);
        notify('Services updated successfully');
        refresh();
      })
      .catch((error) => {
        notify(error.message, 'error');
      });
  };

  const confirmCancel = () => {
    if (window.confirm('Are you sure you would like to cancel?')) {
      setEditServices(false);
    }
  };

  const confirmSubmit = (val) => {
    handleSubmit(val);
  };

  return (
    <>
      <EditServicesHeader />
      <TableRow className="customList schedule_work_section">
        <FormWithRedirect
          resource="scheduled-work"
          record={{ ...record }}
          save={(val) => confirmSubmit(val)}
          mutators={{
            ...arrayMutators,
          }}
          render={({
            handleSubmitWithRedirect,
            loading,
            pristine,
            form: {
              mutators: { push },
            },
          }) => {
            return (
              <TableCell colSpan={9}>
                <FieldArray name="services">
                  {({ fields }) =>
                    fields.map((name, index) => (
                      <Box key={name} display="flex" alignItems="flex-start">
                        <EditService
                          index={index}
                          source={name}
                          record={record}
                          fields={fields}
                        />
                      </Box>
                    ))
                  }
                </FieldArray>
                <FieldArray name="newServices">
                  {({ fields }) =>
                    fields.map((name, index) => (
                      <Box key={name} display="flex" alignItems="flex-start">
                        <EditService
                          index={index}
                          source={name}
                          record={record}
                          fields={fields}
                        />
                      </Box>
                    ))
                  }
                </FieldArray>
                <Grid container spacing={1}>
                  <Grid item md={8} sm={8} xs={8}>
                    <Box mt={1} className={classes.selectRoot}>
                      <UnifiedAddService {...props} push={push} />
                    </Box>
                  </Grid>
                </Grid>
                <Grid item md={12} sm={12} xs={12} className={classes.actionBox}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={2}
                  >
                    <Button
                      label="ra.action.cancel"
                      onClick={confirmCancel}
                      className={classes.btnCancel}
                    >
                      <IconCancel />
                    </Button>
                    <SaveButton
                      handleSubmitWithRedirect={handleSubmitWithRedirect}
                      saving={loading}
                      disabled={pristine}
                    />
                  </Box>
                </Grid>
              </TableCell>
            );
          }}
        />
      </TableRow>
    </>
  );
};

export default UnifiedEditServices;

export const EditService = ({
  index,
  source: sourcePrefix,
  record,
  fields,
  showStatus = true,
  showUpsellStatus = true,
}) => {
  return (
    <>
      <Box className="serviceRow">
        <Box pr={0.8} className="editInput" width="20%">
          <TextInput
            label="Display Name"
            source={`${sourcePrefix}.display_name`}
            validate={[required()]}
          />
        </Box>
        {showStatus && (
          <Box
            pr={0.8}
            className="editInput"
            width="15%"
            display="flex"
            alignItems="center"
          >
            <TextField source={`${sourcePrefix}.status`} record={record} />
          </Box>
        )}
        {showUpsellStatus && (
          <Box pr={0.8} className="editInput" width="24%">
            <SelectInput
              source={`${sourcePrefix}.upsell_status`}
              resettable
              label="Upsell Status"
              choices={[
                { id: 'pending', name: 'Pending' },
                { id: 'approved', name: 'Approved' },
                { id: 'rejected', name: 'Rejected' },
                { id: 'future', name: 'Future' },
              ]}
              allowEmpty
            />
          </Box>
        )}
        <Box pr={0.8} className="editInput" width="15%">
          <NumberInput
            label="Service Duration (Hours)"
            source={`${sourcePrefix}.labor_hours`}
            validate={[required()]}
          />
        </Box>
        <Box pr={0.8} className="editInput" width="15%">
          <NumberInput
            label="Labor Dollars"
            source={`${sourcePrefix}.labor_cost_dollars`}
            validate={[required()]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="editInput" width="15%">
          <NumberInput
            label="Parts Dollars"
            source={`${sourcePrefix}.parts_cost_dollars`}
            validate={[required()]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="editInput" width="3%" mt={2.5} ml={0.63}>
          <DeleteIcon
            style={{ paddingLeft: '20px' }}
            onClick={() => fields.remove(index)}
            fontSize="small"
            cursor="pointer"
          />
        </Box>
      </Box>
    </>
  );
};
